import styled from 'styled-components';

import { BottomTooltip, TipPlacement, HelpTooltip } from 'ui/tooltip';
import { WarningFill } from 'ui/icons';
import { colors, spacing } from 'ui/theme';
import { selectUserProjectId } from 'core/userContext';
import { useSelector } from 'react-redux';
import { SMSContentDefinition } from 'modules/graphql/types';
import { useEditableFieldsContext } from '../context/EditableFieldsContext';
import { useQueryParamContext } from '../context/QueryParamContext';
import {
  getCharLimitPerMsg,
  getIsEditMode,
  getMsgCount,
  getGSM7CharacterLength,
  hasHandlebars,
  isGSM7,
  shortenLinkInMessage,
} from './utils';
import { displayContent } from './content';
import { HandlebarsMessage } from './HandlebarsMessage';
import { ContentType } from './types';
import { useSMSLinkShortening } from '../EditSMS/context/SMSLinkShorteningContext';
import { getFullMessage } from '../EditSMS/utils';

type RestrictionTextProps = {
  textContent?: string;
  messageLength: number;
  characterLimit?: number;
  useGSM?: boolean;
  hasMsgCount?: boolean;
  contentType?: ContentType;
  hasImageUrl?: boolean;
};

const EncodingTextWrapper = styled.div`
  display: flex;
`;

const CharCountWrapper = styled.div`
  display: flex;
  gap: ${spacing.threeEights};
`;

const RestrictionText = ({ textContent = '' }: Pick<RestrictionTextProps, 'textContent'>) => {
  return (
    <div data-test="restriction-text">
      <CharCountWrapper>{textContent}</CharCountWrapper>
    </div>
  );
};

export const TemplateNameRestrictionText = ({
  textContent = '',
  messageLength,
  characterLimit,
}: Pick<RestrictionTextProps, 'textContent' | 'messageLength' | 'characterLimit'>) => {
  const { isEditMode } = useEditableFieldsContext();
  const { view } = useQueryParamContext();
  const isEditModeVisible = getIsEditMode(view, isEditMode);

  if (!isEditModeVisible) return null;

  const displayCharacterCount = () => {
    const currentMessageLength = messageLength;
    const perMessageLimit = characterLimit ?? getCharLimitPerMsg(textContent, messageLength);

    return `${currentMessageLength} / ${perMessageLimit} characters`;
  };

  return (
    <div data-test="template-name-restriction-text">
      <RestrictionText textContent={displayCharacterCount()} />
    </div>
  );
};

export const SMSRestrictionText = ({
  textContent = '',
  useGSM = false,
  hasMsgCount = false,
  hasImageUrl = false,
}: Pick<RestrictionTextProps, 'textContent' | 'useGSM' | 'hasMsgCount' | 'hasImageUrl'>) => {
  const { isEditMode, liveTemplate } = useEditableFieldsContext();
  const { view } = useQueryParamContext();
  const isEditModeVisible = getIsEditMode(view, isEditMode);
  const projectId = useSelector(selectUserProjectId);

  const { isSMSLinkShorteningEnabled, shortlinkLength, defaultSmsLinkShorteningDomain } =
    useSMSLinkShortening();
  const smsData = (liveTemplate?.mediumData || {}) as SMSContentDefinition;
  const { trackingDomain } = smsData;
  const optOutDisclaimer = smsData.optOutDisclaimer?.message || '';

  const fullSmsMessage = getFullMessage(textContent || '', optOutDisclaimer);
  const messageMaybeWithShortenLink =
    shortenLinkInMessage(
      fullSmsMessage,
      trackingDomain ?? defaultSmsLinkShorteningDomain ?? '',
      projectId,
      shortlinkLength,
      isSMSLinkShorteningEnabled,
    ) || '';

  const isContactCardSelected =
    smsData?.verifiedContactCardId && smsData.verifiedContactCardId > -1;

  const segmentCount = getMsgCount(
    messageMaybeWithShortenLink,
    getGSM7CharacterLength(messageMaybeWithShortenLink),
  );

  const typeOfSegment = hasImageUrl || isContactCardSelected ? `MMS` : `SMS`;

  if (!isEditModeVisible) return null;

  if (hasHandlebars(textContent)) {
    return <HandlebarsMessage />;
  }

  const displayCharacterCount = () => {
    const currentMessageLength = getGSM7CharacterLength(messageMaybeWithShortenLink);

    const perMessageLimit = getCharLimitPerMsg(messageMaybeWithShortenLink, currentMessageLength);

    return `${currentMessageLength} / ${perMessageLimit} characters`;
  };

  return (
    <div data-test="sms-restriction-text">
      <RestrictionText textContent={displayCharacterCount()} />
      {hasMsgCount && (
        <div>{displayContent.strSegmentCount({ msgCount: segmentCount, typeOfSegment })}</div>
      )}
      {useGSM && (
        <EncodingTextWrapper>
          {isGSM7(textContent) ? displayContent.gsm7Encoding : displayContent.ucs2Encoding}
          <HelpTooltip
            tooltipContent={displayContent.encodingHelpText}
            placement={TipPlacement.Right}
            iconProps={{
              color: colors.purple90,
              width: spacing.base,
              height: spacing.base,
            }}
          />
        </EncodingTextWrapper>
      )}
    </div>
  );
};

type WhatsAppRestrictionTextProps = {
  textContent: string; // Does not accept null or undefined values
  maxChars?: number;
};

export const WhatsAppRestrictionText = ({
  textContent = '',
  maxChars = 1024,
}: WhatsAppRestrictionTextProps) => {
  const { isEditMode } = useEditableFieldsContext();
  const { view } = useQueryParamContext();
  const isEditModeVisible = getIsEditMode(view, isEditMode);

  if (!isEditModeVisible) return null;

  if (hasHandlebars(textContent)) {
    return <HandlebarsMessage />;
  }

  return <RestrictionText textContent={`${textContent.length} / ${maxChars} characters`} />;
};

export const WebPushRestrictionText = ({
  textContent = '',
  messageLength,
  contentType = ContentType.TITLE,
}: Pick<RestrictionTextProps, 'textContent' | 'messageLength' | 'contentType'>) => {
  const { isEditMode } = useEditableFieldsContext();
  const { view } = useQueryParamContext();
  const isEditModeVisible = getIsEditMode(view, isEditMode);

  const TITLE_MAX = 39;
  const BODY_MAX = 55;
  const CONTENT_CHAR_MAX = contentType === ContentType.TITLE ? TITLE_MAX : BODY_MAX;
  const isWebPushAndCharLimitReached = messageLength > CONTENT_CHAR_MAX;

  if (!isEditModeVisible) return null;

  if (hasHandlebars(textContent)) {
    return <HandlebarsMessage />;
  }

  const displayCharacterCount = () => {
    return displayContent.strCharacterCount({ charCount: messageLength });
  };

  return (
    <div data-test="web-push-restriction-text">
      <BottomTooltip
        tooltipContent={displayContent.tooltipMaxCharLimit}
        isEnabled={isWebPushAndCharLimitReached}
        isUnderlined={isWebPushAndCharLimitReached}
      >
        {isWebPushAndCharLimitReached && <WarningFill isColorInherited={true} />}
        <RestrictionText textContent={displayCharacterCount()} />
      </BottomTooltip>
    </div>
  );
};
